import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { createLinkedInAccount, initiateLinkedInLogin, verifyLinkedInCode, cancelLinkedInVerification, resendLinkedInCode } from '../../store/Integration';

// mode: 'create' | 'login' | 'verify'
const LinkedInLoginModal = ({ isOpen, toggle, mode = 'create', integrationId = null, accountIdentifier = null, onDone }) => {
    const dispatch = useDispatch();
    const linkedInLogin = useSelector(state => state['Integration']?.['linkedInLogin']) || {};
    const loginState = linkedInLogin[integrationId] || {};

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    useEffect(() => {
        if (isOpen && mode === 'login' && accountIdentifier) setEmail(accountIdentifier);
    }, [isOpen, mode, accountIdentifier]);
    const [loginMode, setLoginMode] = useState('headless');
    const [code, setCode] = useState('');
    const [localError, setLocalError] = useState('');

    const resetForm = () => { setName(''); setEmail(''); setPassword(''); setCode(''); setLocalError(''); setLoginMode('headless'); };

    const handleClose = () => { resetForm(); toggle(); };

    const handleCreate = () => {
        if (!name.trim()) return setLocalError('Account name is required');
        if (!email.trim()) return setLocalError('Email is required');
        setLocalError('');
        dispatch(createLinkedInAccount(
            { name: name.trim(), accountIdentifier: email.trim(), loginMode },
            () => { resetForm(); onDone && onDone(); toggle(); },
            (err) => setLocalError(err)
        ));
    };

    const handleLogin = () => {
        if (loginMode === 'headless' && !email.trim()) return setLocalError('Email is required for headless login');
        if (loginMode === 'headless' && !password.trim()) return setLocalError('Password is required for headless login');
        setLocalError('');
        dispatch(initiateLinkedInLogin(
            integrationId,
            loginMode === 'headless' ? { email: email.trim(), password: password.trim() } : {},
            () => { resetForm(); onDone && onDone(); toggle(); },
            (err) => setLocalError(err)
        ));
    };

    const handleVerify = () => {
        if (!code.trim() || code.trim().length < 4) return setLocalError('Please enter a valid verification code');
        setLocalError('');
        dispatch(verifyLinkedInCode(
            integrationId,
            code.trim(),
            () => { resetForm(); onDone && onDone(); toggle(); },
            (err) => setLocalError(err)
        ));
    };

    const handleCancelVerification = () => {
        dispatch(cancelLinkedInVerification(
            integrationId,
            () => { resetForm(); onDone && onDone(); toggle(); },
            () => { resetForm(); toggle(); }
        ));
    };

    const handleLoginAgain = () => {
        resetForm();
        setLiModalMode('login');
    };

    const [resendSuccess, setResendSuccess] = useState(false);

    const handleResendCode = () => {
        setResendSuccess(false);
        dispatch(resendLinkedInCode(
            integrationId,
            () => setResendSuccess(true),
            (err) => setLocalError(err)
        ));
    };

    const [liModalMode, setLiModalMode] = useState(mode);
    useEffect(() => { setLiModalMode(mode); }, [mode]);

    const isLoading = loginState['loading'];

    const titles = { create: 'Add LinkedIn Account', login: 'Login to LinkedIn', verify: 'Enter Verification Code' };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} className="main-modal new-user-modal modal-dialog-centered li-login-modal">
            <ModalHeader toggle={handleClose}><h3>{titles[liModalMode]}</h3></ModalHeader>
            <ModalBody>
                <div className="modal-area">
                    {liModalMode === 'create' && (
                        <>
                            <div className="form-group mb-3">
                                <label className="form-label">Account Name</label>
                                <div className="li-input-wrapper">
                                    <i className="fa fa-user li-input-icon"></i>
                                    <input type="text" className="form-control li-input-with-icon" placeholder="e.g. John Doe" value={name} onChange={e => setName(e.target.value)} disabled={isLoading} />
                                </div>
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label">LinkedIn Email</label>
                                <div className="li-input-wrapper">
                                    <i className="fa fa-envelope li-input-icon"></i>
                                    <input type="email" className="form-control li-input-with-icon" placeholder="john@example.com" value={email} onChange={e => setEmail(e.target.value)} disabled={isLoading} />
                                </div>
                            </div>
                        </>
                    )}

                    {liModalMode === 'login' && (
                        <>
                            <div className="form-group mb-3">
                                <label className="form-label">LinkedIn Email</label>
                                <div className="li-input-wrapper">
                                    <i className="fa fa-envelope li-input-icon"></i>
                                    <input type="email" className="form-control li-input-with-icon" placeholder="john@example.com" value={email} onChange={e => setEmail(e.target.value)} disabled={isLoading} />
                                </div>
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label">Password</label>
                                <div className="li-input-wrapper">
                                    <i className="fa fa-lock li-input-icon"></i>
                                    <input type="password" className="form-control li-input-with-icon" placeholder="LinkedIn password" value={password} onChange={e => setPassword(e.target.value)} disabled={isLoading} />
                                </div>
                            </div>
                        </>
                    )}

                    {liModalMode === 'verify' && (
                        <div className="form-group mb-3">
                            <p className="li-verify-hint">LinkedIn sent a verification code to your email or phone. Enter it below.</p>
                            <input
                                autoFocus
                                type="text"
                                value={code}
                                maxLength={8}
                                disabled={isLoading}
                                placeholder="6-digit code"
                                className="form-control li-code-input"
                                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                            />
                            <div className="li-resend-row">
                                <span className="li-verify-hint mb-0">Didn't receive the code?</span>
                                <button type="button" className="li-resend-btn" onClick={handleResendCode} disabled={isLoading}>
                                    <i className="fa fa-repeat mr-1"></i> Resend Code
                                </button>
                                {resendSuccess && <span className="li-resend-success"><i className="fa fa-check mr-1"></i> Code resent</span>}
                            </div>
                        </div>
                    )}

                    {(localError || loginState['error']) && <p className="li-modal-error">{localError || loginState['error']}</p>}
                </div>

                {liModalMode === 'verify' ? (
                    <div className="group-btns mt-4 d-flex justify-content-between">
                        <div>
                            <button className="btn-style-border mr-2" onClick={handleLoginAgain} disabled={isLoading} title="Restart the login flow">
                                <i className="fa fa-refresh mr-1"></i> Login Again
                            </button>
                            <button className="btn-style-border" onClick={handleCancelVerification} disabled={isLoading} title="Cancel and reset to needs login">
                                <i className="fa fa-times mr-1"></i> Cancel Login
                            </button>
                        </div>
                        <button className="btn-style-new" onClick={handleVerify} disabled={isLoading}>
                            {isLoading ? <><i className="fa fa-spinner fa-spin mr-1"></i> Verifying...</> : 'Submit Code'}
                        </button>
                    </div>
                ) : (
                    <div className="group-btns mt-4 d-flex justify-content-end">
                        <button className="btn-style-border mr-2" onClick={handleClose} disabled={isLoading}>Cancel</button>
                        {liModalMode === 'create' && (
                            <button className="btn-style-new" onClick={handleCreate} disabled={isLoading}>
                                {isLoading ? <><i className="fa fa-spinner fa-spin mr-1"></i> Creating...</> : 'Create Account'}
                            </button>
                        )}
                        {liModalMode === 'login' && (
                            <button className="btn-style-new" onClick={handleLogin} disabled={isLoading}>
                                {isLoading ? <><i className="fa fa-spinner fa-spin mr-1"></i> Logging in...</> : 'Login'}
                            </button>
                        )}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default LinkedInLoginModal;
